
import { getMobileDeviceInfo } from '../cacheVerification';
import type { VersionInfo } from './versionManager';

export const performReload = (currentInfo: VersionInfo): void => {
  try {
    // Update stored version before reload to prevent loops
    localStorage.setItem('version_info', JSON.stringify(currentInfo));
    
    const { isSafari, isAndroid, isIOS } = getMobileDeviceInfo();
    
    // Clear application cache if available
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Clear service workers if present
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        for (const registration of registrations) {
          registration.unregister();
        }
      });
    }
    
    // Clear local storage except for crucial items
    const keysToKeep = ['version_info', 'user_preferences', 'auth_session'];
    const keysToDelete = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keysToKeep.includes(key)) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => localStorage.removeItem(key));
    
    // Build a cache-busting URL
    const url = window.location.href.split('?')[0];
    const cacheBuster = `_cache=${currentInfo.buildId}-${Date.now()}`;
    const separator = url.includes('?') ? '&' : '?';
    const newUrl = `${url}${separator}${cacheBuster}`;
    
    // Use history.replaceState to silently update the URL with cache busting parameter
    try {
      window.history.replaceState({}, document.title, newUrl);
    } catch (e) {
      console.error('Failed to update URL:', e);
    }
    
    // Special handling for problematic browsers
    if (isSafari || isAndroid || isIOS) {
      // Use location.href instead of reload() for better cache busting
      window.location.href = newUrl;
    } else {
      // Force reload with cache clearing - TypeScript compatible version
      window.location.reload(); // Removed the argument which caused the TypeScript error
      
      // Alternative approach: use location.href for all browsers to ensure cache busting
      // window.location.href = newUrl;
    }
  } catch (error) {
    console.error('Reload error:', error);
    // Last resort - basic reload
    window.location.reload();
  }
};

// This function is now moved to versionManager.ts to avoid circular dependencies
// Just keeping this as an implementation detail
export const forceUpdateImplementation = (currentInfo: VersionInfo): void => {
  performReload(currentInfo);
};
